import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { formatCurrency } from '../lib/utils';
import { DailyAvailable } from './DailyAvailable';
import { QuickAddFAB } from './QuickAdd';
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target,
  Calendar,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

export function Dashboard() {
  const { 
    state, 
    getAccounts, 
    getCategories, 
    getTransactions, 
    getBudgetAllocations 
  } = useApp();

  const accounts = getAccounts();
  const categories = getCategories();
  const transactions = getTransactions();
  const budgetAllocations = getBudgetAllocations();

  // Calculate summary data
  const totalBalance = accounts.reduce((sum, account) => sum + (account.balance || 0), 0);
  const totalAllocated = budgetAllocations.reduce((sum, allocation) => sum + allocation.allocated, 0);
  const totalSpent = Math.abs(budgetAllocations.reduce((sum, allocation) => sum + allocation.spent, 0));
  const totalAvailable = totalAllocated - totalSpent;

  // Calculate monthly income (positive transactions)
  const monthlyIncome = transactions
    .filter(t => t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);
  
  const toBeAllocated = monthlyIncome - totalAllocated;

  // Get overspent categories
  const overspentCategories = budgetAllocations.filter(allocation => {
    const available = allocation.allocated + allocation.rollover - Math.abs(allocation.spent);
    return available < 0;
  });

  // Get category details for budget display
  const budgetWithCategories = budgetAllocations.map(allocation => {
    const category = categories.find(c => c.id === allocation.categoryId);
    const available = allocation.allocated + allocation.rollover - Math.abs(allocation.spent);
    const progressPercent = allocation.allocated > 0 
      ? Math.min((Math.abs(allocation.spent) / allocation.allocated) * 100, 100)
      : 0;

    return {
      ...allocation,
      categoryName: category?.name || 'Unknown Category',
      available,
      progressPercent
    };
  }).slice(0, 6); // Show first 6 categories

  return (
    <div className="py-4 sm:py-8 space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1 sm:space-y-2">
          <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm sm:text-lg text-muted-foreground">
            Welcome back, <span className="text-primary font-semibold">{state.auth.currentUser?.display}</span>
          </p>
        </div>
        <Link to="/add-transaction" className="w-full sm:w-auto">
          <Button className="btn-mobile-primary btn-gradient shadow-lg w-full sm:w-auto" data-testid="quick-add-transaction">
            <Plus className="w-4 h-4 mr-2" />
            Add Transaction
          </Button>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="mobile-grid grid gap-4 sm:gap-6">
        {/* Daily Available - Prominent placement */}
        <div className="sm:col-span-2 lg:col-span-1">
          <DailyAvailable />
        </div>
        <Card className="card-hover shadow-soft">
          <CardHeader className="mobile-card-header flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
            <CardTitle className="mobile-text-sm font-semibold text-muted-foreground">Total Balance</CardTitle>
            <div className="touch-target bg-emerald-500/10 rounded-lg">
              <DollarSign className="h-4 w-4 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent className="mobile-card-content">
            <div className="mobile-text-2xl font-bold amount-positive" data-testid="total-balance">
              {formatCurrency(totalBalance)}
            </div>
            <p className="mobile-text-sm text-muted-foreground mt-1">Across all accounts</p>
          </CardContent>
        </Card>

        <Card className="card-hover shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
            <CardTitle className="text-xs sm:text-sm font-semibold text-muted-foreground">Monthly Budget</CardTitle>
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg">
              <Target className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="text-xl sm:text-3xl font-bold text-foreground" data-testid="monthly-allocated">
              {formatCurrency(totalAllocated)}
            </div>
            <p className={`text-xs sm:text-sm mt-1 font-medium ${toBeAllocated > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {toBeAllocated > 0 ? `${formatCurrency(toBeAllocated)} to allocate` : 'Fully allocated'}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
            <CardTitle className="text-xs sm:text-sm font-semibold text-muted-foreground">Monthly Spent</CardTitle>
            <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg">
              <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="text-xl sm:text-3xl font-bold text-foreground" data-testid="monthly-spent">
              {formatCurrency(totalSpent)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">This month</p>
          </CardContent>
        </Card>

        <Card className="card-hover shadow-soft sm:col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
            <CardTitle className="text-xs sm:text-sm font-semibold text-muted-foreground">Available</CardTitle>
            <div className="p-1.5 sm:p-2 bg-purple-500/10 rounded-lg">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className={`text-xl sm:text-3xl font-bold ${totalAvailable >= 0 ? 'amount-positive' : 'amount-negative'}`} data-testid="monthly-available">
              {formatCurrency(totalAvailable)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Budget remaining</p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Overview */}
      <Card className="shadow-soft animate-slide-up">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="flex items-center justify-between text-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <span>Budget Overview - {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            </div>
            <Link to="/budget">
              <Button variant="outline" size="sm" className="hover:bg-primary hover:text-primary-foreground transition-all">
                View Full Budget
              </Button>
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            {budgetWithCategories.map((item, index) => (
              <div key={item.categoryId} className="space-y-3 animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{item.categoryName}</span>
                  <span className="text-sm font-mono text-muted-foreground">
                    <span className="font-semibold">{formatCurrency(Math.abs(item.spent))}</span>
                    <span className="mx-1">/</span>
                    <span>{formatCurrency(item.allocated)}</span>
                  </span>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={item.progressPercent} 
                    className={`h-3 ${item.available < 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-muted'}`}
                  />
                  <div className="flex justify-between items-center text-sm">
                    <span className={`font-medium ${item.available >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {item.available >= 0 ? 'Remaining:' : 'Overspent:'}
                    </span>
                    <span className={`font-semibold ${item.available >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(item.available))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Accounts */}
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
              <span className="text-base sm:text-lg">Account Balances</span>
              <Link to="/accounts" className="w-full sm:w-auto">
                <Button variant="outline" size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
                  View All
                </Button>
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 sm:space-y-3" data-testid="account-list">
              {accounts.slice(0, 4).map((account) => (
                <div key={account.id} className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div>
                    <p className="font-medium text-sm sm:text-base">{account.name}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground capitalize">
                      {account.type}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium text-sm sm:text-base ${(account.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(account.balance || 0)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alerts & Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Alerts & Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* To Be Allocated Alert */}
              {toBeAllocated > 0 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      Money to Allocate
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      You have {formatCurrency(toBeAllocated)} unallocated
                    </p>
                  </div>
                </div>
              )}

              {/* Overspending Alert */}
              {overspentCategories.length > 0 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-200">
                      Overspending Alert
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {overspentCategories.length} categories are over budget
                    </p>
                  </div>
                </div>
              )}

              {/* All Good Status */}
              {toBeAllocated === 0 && overspentCategories.length === 0 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-200">
                      Budget on Track
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      All categories are properly allocated and within budget
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="shadow-soft animate-slide-up">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="flex items-center space-x-3 text-xl">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Plus className="w-5 h-5 text-primary" />
            </div>
            <span>Quick Actions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/add-transaction">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground transition-all duration-200 group">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary-foreground/10 transition-colors">
                  <Plus className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Add Transaction</span>
              </Button>
            </Link>
            
            <Link to="/budget">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground transition-all duration-200 group">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary-foreground/10 transition-colors">
                  <Target className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Budget</span>
              </Button>
            </Link>
            
            <Link to="/accounts">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground transition-all duration-200 group">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary-foreground/10 transition-colors">
                  <DollarSign className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Accounts</span>
              </Button>
            </Link>
            
            <Link to="/reports">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground transition-all duration-200 group">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary-foreground/10 transition-colors">
                  <Calendar className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Reports</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Add Floating Action Button */}
      <QuickAddFAB />
    </div>
  );
}